/* ── PAG Insights column definitions ──────────────────────────────────
   Column configs for the three DataTable instances on the Insights page:
   the main inventory table, the chart drill-down drawer, and the review
   detail dialog.  Each builder returns a memoizable array and accepts only
   the dynamic bits it needs. */

// External Libraries
import { Info } from "lucide-react";
import React from "react";

// Internal Modules
import type { Column } from "../../../components/DataTable";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";

// Relative Imports
import { ReviewBadge, StatusPill, TextCell } from "../components/StatusBadges";
import { RISK_PILL_TONES } from "../constants/pagInsightsConstants";
import type { PagAccountReview, PagAccountRow } from "../services/pagApi";
import {
  formatEpochDate,
  formatInventoryDate,
  hasOpenReviewRequest,
  isAccountOverdue,
  isDateOverdue,
  pamStatusTone,
} from "./pagHelpers";

/* ── Main inventory table ────────────────────────────────────────── */

export type AccountColumnsDeps = {
  allSelected?: boolean;
  someSelected?: boolean;
  selectedIds?: string[];
  selectableAccounts?: PagAccountRow[];
  toggleAll?: () => void;
  toggleRow?: (name: string) => void;
  openAccountDetails?: (row: PagAccountRow) => void;
  hideSelect?: boolean;
  hideReview?: boolean;
  hideDetail?: boolean;
};

export const buildAccountColumns = ({
  allSelected = false,
  someSelected = false,
  selectedIds = [],
  selectableAccounts = [],
  toggleAll = () => {},
  toggleRow = () => {},
  openAccountDetails,
  hideSelect = false,
  hideReview = false,
  hideDetail = false,
}: AccountColumnsDeps = {}): Column<PagAccountRow>[] => {
  const columns: Column<PagAccountRow>[] = [];

  if (!hideSelect) {
    columns.push({
      key: "select",
      header: (
        <div className="flex items-center justify-center p-0.5">
          <Checkbox
            checked={allSelected ? true : someSelected ? "indeterminate" : false}
            onCheckedChange={() => toggleAll()}
            disabled={selectableAccounts.length === 0}
            aria-label="Select all accounts without an open review"
          />
        </div>
      ),
      align: "center",
      headerAlign: "center",
      width: "64px",
      minWidth: 64,
      headerClassName: "!px-2",
      cellClassName: "!px-2 !overflow-visible",
      tooltip: false,
      render: (row) => (
        <div className="flex items-center justify-center p-0.5">
          <Checkbox
            checked={selectedIds.includes(row.accountName)}
            onCheckedChange={() => toggleRow(row.accountName)}
            disabled={hasOpenReviewRequest(row.reviewStatus)}
            aria-label={`Select ${row.accountName}`}
          />
        </div>
      ),
    });
  }

  columns.push(
    {
      key: "accountName",
    header: "Account",
    align: "left",
    headerAlign: "center",
    width: "220px",
    minWidth: 220,
    sortable: true,
    tooltip: (row) =>
      row.displayName ? `${row.accountName} (${row.displayName})` : row.accountName,
    render: (row) => (
      <span className="whitespace-nowrap break-normal text-sm text-foreground">
        {row.accountName}
      </span>
    ),
  },
  {
    key: "groupNames",
    header: "Group",
    align: "left",
    headerAlign: "center",
    width: "270px",
    minWidth: 270,
    sortable: false,
    tooltip: (row) => row.groupNames?.join(", ") || undefined,
    render: (row) => (
      <div className="flex flex-wrap gap-1">
        {row.groupNames?.length ? (
          row.groupNames.map((group) => (
            <span
              key={group}
              className="whitespace-nowrap break-normal rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/15 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:text-indigo-300"
            >
              {group}
            </span>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )}
      </div>
    ),
  },
  {
    key: "sourceSystemType",
    header: "Source",
    align: "left",
    headerAlign: "center",
    width: "160px",
    minWidth: 140,
    sortable: true,
    tooltip: (row) => row.sourceSystemType ?? undefined,
    render: (row) => <TextCell value={row.sourceSystemType ?? null} />,
  },
  {
    key: "sourceSystemName",
    header: "Resources",
    align: "left",
    headerAlign: "center",
    width: "160px",
    minWidth: 140,
    sortable: true,
    ellipsis: 2,
    tooltip: (row) => row.sourceSystemName,
    render: (row) => <TextCell value={row.sourceSystemName} />,
  },
  {
    key: "lastLogin",
    header: "Last Login",
    align: "center",
    headerAlign: "center",
    width: "170px",
    minWidth: 165,
    sortable: true,
    render: (row) => (
      <div className="flex w-full items-center justify-center text-center">
        <TextCell value={formatEpochDate(row.lastLogin)} />
      </div>
    ),
  },
  {
    key: "enabled",
    header: "Account Status",
    align: "center",
    headerAlign: "center",
    width: "150px",
    minWidth: 150,
    sortable: true,
    render: (row) => (
      <StatusPill label={row.enabled ? "Enabled" : "Disabled"} tone={row.enabled ? "emerald" : "slate"} />
    ),
  },
  {
    key: "riskLevel",
    header: "Risk",
    align: "center",
    headerAlign: "center",
    width: "130px",
    minWidth: 130,
    sortable: true,
    render: (row) => {
      const level = row.riskLevel;
      /* The API sends it display-cased; the tone map is keyed by enum. */
      const key = level?.toUpperCase() ?? "";
      if (!level || key === "UNCATEGORISED") {
        return <StatusPill label="Unassigned" tone="slate" />;
      }
      return <StatusPill label={level} tone={RISK_PILL_TONES[key] ?? "slate"} />;
    },
  },
  {
    key: "pamStatus",
    header: "PAM Status",
    align: "center",
    headerAlign: "center",
    width: "150px",
    minWidth: 150,
    sortable: true,
    render: (row) =>
      row.pamStatus ? (
        <StatusPill label={row.pamStatus} tone={pamStatusTone(row.pamStatus)} />
      ) : (
        <TextCell value={null} />
      ),
  },
  );

  if (!hideReview) {
    columns.push({
      key: "reviewStatus",
      header: "Review",
      align: "center",
      headerAlign: "center",
      width: "175px",
      minWidth: 175,
      sortable: true,
      render: (row) => {
        const overdue = isAccountOverdue(row);
        return <ReviewBadge review={row.reviewStatus} isOverdue={overdue} />;
      },
    });
  }

  if (!hideDetail && openAccountDetails) {
    columns.push({
      key: "detail",
      header: "Detail",
      align: "center",
      headerAlign: "center",
      width: "90px",
      minWidth: 90,
      tooltip: false,
      render: (row) => (
        <Button
          variant="indigoSubtle"
          size="icon"
          type="button"
          onClick={() => {
            openAccountDetails(row);
          }}
          className="size-7 justify-center rounded-md hover:bg-indigo-500/10 hover:text-indigo-300"
          aria-label={`Review request details for ${row.accountName}`}
        >
          <Info className="size-4" />
        </Button>
      ),
    });
  }

  return columns;
};

/* ── Drill-down table (charts) ───────────────────────────────────── */

export const buildDrilldownColumns = (
  accountColumns: Column<PagAccountRow>[],
  options?: { hideReview?: boolean; hideDetail?: boolean },
): Column<PagAccountRow>[] =>
  accountColumns.filter((column) => {
    if (column.key === "select") return false;
    if (column.key === "detail") return false;
    if (options?.hideReview && column.key === "reviewStatus") return false;
    return true;
  });

/* ── Review detail dialog ────────────────────────────────────────── */

export const buildDetailColumns = (): Column<PagAccountReview>[] => [
  {
    key: "reviewRef",
    header: "Review Ref",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.reviewRef} />,
  },
  {
    key: "requestedBy",
    header: "Requested By",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.requestedBy} />,
  },
  {
    key: "requestComment",
    header: "Request Comment",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.requestComment} wrap />,
  },
  {
    key: "actionPlan",
    header: "Action Plan",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.actionPlan} />,
  },
  {
    key: "dueDate",
    header: "Due Date",
    align: "left",
    headerAlign: "center",
    render: (row) => {
      const formatted = formatInventoryDate(row.dueDate);
      if (!formatted) return <TextCell value={null} />;
      const overdue =
        row.status !== "Reviewed" &&
        row.status !== "Revoked" &&
        isDateOverdue(row.dueDate);
      return (
        <span
          className={`whitespace-nowrap break-normal text-sm ${
            overdue ? "font-semibold text-red-500 dark:text-red-400" : "text-foreground"
          }`}
        >
          {formatted}
        </span>
      );
    },
  },
  {
    key: "reviewedAt",
    header: "Reviewed On",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={formatInventoryDate(row.reviewedAt)} />,
  },
  {
    key: "comment",
    header: "Comment",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.comment} wrap />,
  },
  {
    key: "commentedBy",
    header: "Commented By",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={row.commentedBy} />,
  },
  {
    key: "commentedOn",
    header: "Commented Date",
    align: "left",
    headerAlign: "center",
    render: (row) => <TextCell value={formatInventoryDate(row.commentedOn)} />,
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    headerAlign: "center",
    render: (row) => <ReviewBadge review={row.status} />,
  },
];
